import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Modal, 
  Button, 
  Table, 
  Card, 
  Badge, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Row, 
  Col,
  Pagination
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { cameraAPI, classroomAPI, edgeDeviceAPI, clearCache } from '../utils/api';
import type { Camera, CreateCameraRequest, UpdateCameraRequest } from '../types/camera';
import type { Classroom } from '../types/classroom';
import type { EdgeDevice } from '../types/edgedevice';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';

// Lightweight icon replacements to reduce bundle size
const CameraIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2z"/>
    <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0 1a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-primary" style={{ cursor: 'pointer' }}>
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
  </svg>
);



const PersonIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
  </svg>
);



const PlusIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-primary" style={{ cursor: 'pointer' }}>
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

// Add sort icons
const SortIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3.5 2.5a.5.5 0 0 0-1 0v8.793l-1.146-1.147a.5.5 0 0 0-.708.708l2 1.5a.5.5 0 0 0 .708 0l2-1.5a.5.5 0 0 0-.708-.708L3.5 11.293V2.5zm3.5 1a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM7.5 6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zm0 3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z"/>
  </svg>
);

const SortUpIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3.5 12.5a.5.5 0 0 1-1 0V3.707L1.354 4.854a.5.5 0 1 1-.708-.708l2-1.5a.5.5 0 0 1 .708 0l2 1.5a.5.5 0 0 1-.708.708L3.5 3.707V12.5zm3.5-9a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM7.5 6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zm0 3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z"/>
  </svg>
);

const SortDownIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3.5 2.5a.5.5 0 0 0-1 0v8.793l-1.146-1.147a.5.5 0 0 0-.708.708l2 1.5a.5.5 0 0 0 .708 0l2-1.5a.5.5 0 0 0-.708-.708L3.5 11.293V2.5zm3.5 1a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM7.5 6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zm0 3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z"/>
  </svg>
);

// Memoized Camera Card Component for Mobile
const CameraCard = memo(({ camera, onViewDetails, onEdit, modalLoading, canEdit }: {
  camera: Camera;
  onViewDetails: (id: number) => void;
  onEdit: (camera: Camera) => void;
  modalLoading: boolean;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInNurseryTimezone(dateString, currentNursery?.timezone || 'UTC');
  }, [currentNursery?.timezone]);

  return (
    <Card className="mobile-camera-card m-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <CameraIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(camera.name)}</h6>
              <small className="text-muted">ID: {renderCellValue(camera.id)}</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {canEdit && (
              <span
                onClick={() => onEdit(camera)}
                style={{ cursor: 'pointer' }}
                title={t('editCamera')}
              >
                <EditIcon />
              </span>
            )}
            <Badge bg={camera.status ? 'success' : 'danger'}>
              {camera.status ? tc('status.active') : tc('status.inactive')}
            </Badge>
          </div>
        </div>
        
        <Row className="g-2 mb-3">
          <Col xs={12}>
            <small className="text-muted d-block">{t('cameraNumber')}</small>
            <span className="fw-medium">{renderCellValue(camera.camera_number)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">
              <LocationIcon />
              <span className="ms-1">{t('location')}</span>
            </small>
            <span>{renderCellValue(camera.location)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{tc('labels.classroom')}</small>
            <span>{renderCellValue(camera.classroom_name)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{t('lastImage')}</small>
            <span>{camera.latest_image_timestamp ? formatDateTime(camera.latest_image_timestamp) : tc('messages.na')}</span>
          </Col>
        </Row>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {camera.created_at ? formatDateTime(camera.created_at) : tc('messages.na')}
          </small>
          <span
            onClick={() => onViewDetails(camera.id)}
            style={{ cursor: modalLoading ? 'not-allowed' : 'pointer', opacity: modalLoading ? 0.5 : 1 }}
            title={t('viewDetails')}
            className="d-flex align-items-center"
          >
            <EyeIcon />
            <span className="ms-1">{t('viewDetails')}</span>
          </span>
        </div>
      </Card.Body>
    </Card>
  );
});

CameraCard.displayName = 'CameraCard';

// Memoized Table Row Component
const CameraTableRow = memo(({ 
  camera, 
  onViewDetails,
  onEdit, 
  modalLoading,
  nurseryTimezone,
  canEdit
}: { 
  camera: Camera; 
  onViewDetails: (id: number) => void;
  onEdit: (camera: Camera) => void; 
  modalLoading: boolean;
  nurseryTimezone: string;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInNurseryTimezone(dateString, nurseryTimezone);
  }, [nurseryTimezone, tc]);

  return (
    <tr>
      <td>
        <Badge bg={camera.status ? 'success' : 'danger'}>
          {camera.status ? tc('status.active') : tc('status.inactive')}
        </Badge>
      </td>
      <td>
        <div className="d-flex align-items-center">
          <CameraIcon />
          <span className="fw-medium">{renderCellValue(camera.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(camera.camera_number)}</code></td>
      <td>{renderCellValue(camera.location)}</td>
      <td>{renderCellValue(camera.classroom_name)}</td>
      <td>
        <small className="text-muted">
          {camera.latest_image_timestamp ? formatDateTime(camera.latest_image_timestamp) : tc('messages.na')}
        </small>
      </td>
      <td>
        <div className="d-flex gap-2">
          {canEdit && (
            <span
              onClick={() => onEdit(camera)}
              style={{ cursor: 'pointer' }}
              title={t('editCamera')}
            >
              <EditIcon />
            </span>
          )}
          <span
            onClick={() => onViewDetails(camera.id)}
            style={{ cursor: modalLoading ? 'not-allowed' : 'pointer', opacity: modalLoading ? 0.5 : 1 }}
            title={t('viewDetails')}
          >
            <EyeIcon />
          </span>
        </div>
      </td>
    </tr>
  );
});

CameraTableRow.displayName = 'CameraTableRow';

const Cameras: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canAdd, canEdit } = useRolePermissions();
  
  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sort state
  const [sortField, setSortField] = useState<keyof Camera>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Classroom and Edge Device data for form dropdowns
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [edgeDevices, setEdgeDevices] = useState<EdgeDevice[]>([]);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const [edgeDevicesLoading, setEdgeDevicesLoading] = useState(false);
  
  // Create Camera Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalLoading, setCreateModalLoading] = useState(false);
  const [createModalError, setCreateModalError] = useState<string | null>(null);
  const [createModalSuccess, setCreateModalSuccess] = useState<string | null>(null);
  
  // Edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateCameraRequest>({
    edge_device_id: 0,
    name: '',
    nursery_id: currentNursery?.nursery_id || '',
    classroom_id: 0,
    ip_address: '',
    port: 554,
    username: '',
    password: '',
    status: true,
    camera_number: '',
    location: ''
  });

  const fetchCameras = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);
      const response = await cameraAPI.getCameras(currentNursery.nursery_id);
      
      if (response.success) {
        setCameras(response.cameras || []);
      } else {
        setError(t('fetchFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching cameras:', err);
      setError(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery]);

  const fetchCameraDetails = useCallback(async (id: number) => {
    try {
      setModalLoading(true);
      const response = await cameraAPI.getCameraDetails(id);
      
      if (response.success) {
        setSelectedCamera(response.camera);
        setModalOpen(true);
      } else {
        setError(t('detailsFetchFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching camera details:', err);
      setError(t('detailsLoadFailed'));
    } finally {
      setModalLoading(false);
    }
  }, []);

  const loadClassrooms = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setClassroomsLoading(true);
      const response = await classroomAPI.getClassrooms(currentNursery.nursery_id);
      setClassrooms(response.classrooms || []);
    } catch (error: any) {
      console.error('Error fetching classrooms:', error);
    } finally {
      setClassroomsLoading(false);
    }
  }, [currentNursery]);

  const loadEdgeDevices = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setEdgeDevicesLoading(true);
      const response = await edgeDeviceAPI.getEdgeDevices(currentNursery.nursery_id);
      // Handle both array response and object response from API
      const devices = Array.isArray(response) ? response : (response.devices || []);
      setEdgeDevices(devices);
    } catch (error: any) {
      console.error('Error fetching edge devices:', error);
    } finally {
      setEdgeDevicesLoading(false);
    }
  }, [currentNursery]);



  useEffect(() => {
    if (currentNursery) {
      fetchCameras();
      loadClassrooms();
      loadEdgeDevices();
    } else {
      setCameras([]);
      setClassrooms([]);
      setEdgeDevices([]);
    }
  }, [currentNursery, fetchCameras, loadClassrooms, loadEdgeDevices]);

  // Update nursery_id in form when currentNursery changes
  useEffect(() => {
    if (currentNursery) {
      setFormData(prev => ({ ...prev, nursery_id: currentNursery.nursery_id }));
    }
  }, [currentNursery]);

  // Sort options
  const sortOptions = [
    { value: 'name', label: t('cameraName') },
    { value: 'camera_number', label: t('cameraNumber') },
    { value: 'location', label: t('location') },
    { value: 'classroom_name', label: tc('labels.classroom') },
    { value: 'status', label: tc('labels.status') },
    { value: 'latest_image_timestamp', label: t('lastImageShort') }
  ];

  // Handle sort change
  const handleSortChange = useCallback((field: keyof Camera) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting
  }, [sortField]);

  // Get sort icon for a column
  const getSortIcon = useCallback((field: keyof Camera) => {
    if (sortField !== field) return <SortIcon />;
    return sortDirection === 'asc' ? <SortUpIcon /> : <SortDownIcon />;
  }, [sortField, sortDirection]);

  // Reset page when search term or sort changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortField, sortDirection]);

  // Filter cameras based on search term
  const filteredCameras = useMemo(() => {
    if (!searchTerm.trim()) return cameras;

    const searchLower = searchTerm.toLowerCase();
    return cameras.filter((camera) => {
      return (
        camera.name?.toLowerCase().includes(searchLower) ||
        camera.camera_number?.toLowerCase().includes(searchLower) ||
        camera.location?.toLowerCase().includes(searchLower) ||
        camera.classroom_name?.toLowerCase().includes(searchLower) ||
        (camera.status ? 'active' : 'inactive').includes(searchLower)
      );
    });
  }, [cameras, searchTerm]);

  // Sort filtered cameras
  const sortedCameras = useMemo(() => {
    return [...filteredCameras].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle boolean values (status)
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      // Handle date values
      if (sortField === 'latest_image_timestamp') {
        aValue = (aValue && typeof aValue === 'string') ? new Date(aValue).getTime() : 0;
        bValue = (bValue && typeof bValue === 'string') ? new Date(bValue).getTime() : 0;
      }

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [filteredCameras, sortField, sortDirection]);

  // Paginate sorted cameras
  const totalPages = Math.ceil(sortedCameras.length / rowsPerPage);
  const paginatedCameras = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedCameras.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedCameras, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Create Camera Modal handlers
  const handleShowCreateModal = useCallback(() => {
    setIsEditMode(false);
    setEditingCamera(null);
    setShowCreateModal(true);
    setCreateModalError(null);
    setCreateModalSuccess(null);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreateModalError(null);
    setCreateModalSuccess(null);
    setIsEditMode(false);
    setEditingCamera(null);
    setFormData({
      edge_device_id: 0,
      name: '',
      nursery_id: currentNursery?.nursery_id || '',
      classroom_id: 0,
      ip_address: '',
      port: 554,
      username: '',
      password: '',
      status: true,
      camera_number: '',
      location: ''
    });
  }, [currentNursery]);

  const handleFormChange = useCallback((field: keyof CreateCameraRequest, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentNursery) return;

    try {
      setCreateModalLoading(true);
      setCreateModalError(null);
      setCreateModalSuccess(null);

      if (isEditMode && editingCamera) {
        // Update existing camera
        const updateData: UpdateCameraRequest = {
          edge_device_id: formData.edge_device_id,
          name: formData.name,
          classroom_id: formData.classroom_id,
          ip_address: formData.ip_address,
          port: formData.port,
          username: formData.username,
          password: formData.password,
          status: formData.status,
          location: formData.location
        };
        
        const response = await cameraAPI.updateCamera(
          editingCamera.id, 
          updateData, 
          currentNursery.nursery_id
        );
        
        setCreateModalSuccess(response.message || t('updatedSuccess'));
      } else {
        // Create new camera
        const response = await cameraAPI.createCamera(formData);
        setCreateModalSuccess(response.message || t('createdSuccess'));
      }
      
      // Refresh the list
      await fetchCameras();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseCreateModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error saving camera:', error);
      
      // Handle API errors
      if (error.response?.data?.message) {
        setCreateModalError(error.response.data.message);
      } else {
        setCreateModalError(error.message || t('saveFailed'));
      }
    } finally {
      setCreateModalLoading(false);
    }
  }, [formData, currentNursery, fetchCameras, handleCloseCreateModal, isEditMode, editingCamera]);

  const handleViewDetails = useCallback((id: number) => {
    fetchCameraDetails(id);
  }, [fetchCameraDetails]);

  const handleEditCamera = useCallback((camera: Camera) => {
    setIsEditMode(true);
    setEditingCamera(camera);
    setShowCreateModal(true);
    setCreateModalError(null);
    setCreateModalSuccess(null);
    setFormData({
      edge_device_id: camera.edge_device_id || 0,
      name: camera.name || '',
      nursery_id: camera.nursery_id || currentNursery?.nursery_id || '',
      classroom_id: camera.classroom_id || 0,
      ip_address: camera.ip_address || '',
      port: camera.port || 554,
      username: camera.username || '',
      password: camera.password || '',
      status: camera.status,
      camera_number: camera.camera_number || '',
      location: camera.location || ''
    });
  }, [currentNursery]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCamera(null);
  }, []);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInNurseryTimezone(dateString, currentNursery?.timezone || 'UTC');
  }, [currentNursery?.timezone]);

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when nursery changes
  useEffect(() => {
    if (currentNursery) {
      clearCache(`cameras_${currentNursery.nursery_id}`);
      setSearchTerm('');
      setPage(1);
      setSortField('name');
      setSortDirection('asc');
    }
  }, [currentNursery]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">
            {t('title')}
          </h2>
          {currentNursery && canAdd('cameras') && (
            <Button variant="primary" onClick={handleShowCreateModal}>
              <PlusIcon />
              {t('addCamera')}
            </Button>
          )}
        </div>
        
        {currentNursery && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentNursery.description} (ID: {currentNursery.nursery_id})
          </Alert>
        )}

        {!currentNursery && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectNursery', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search and Sort Controls */}
        {currentNursery && (
          <Card className="mb-4">
            <Card.Body>
              <Row className="g-3">
                <Col md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <SearchIcon />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={clearSearch}>
                        <ClearIcon />
                      </Button>
                    )}
                  </InputGroup>
                  {searchTerm && (
                    <small className="text-muted mt-2 d-block">
                      {t('found', { count: filteredCameras.length, term: searchTerm })}
                    </small>
                  )}
                </Col>
                <Col md={4}>
                  <div className="d-flex gap-2">
                    <Form.Select
                      size="sm"
                      value={sortField}
                      onChange={(e) => handleSortChange(e.target.value as keyof Camera)}
                      style={{ minWidth: '150px' }}
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleSortChange(sortField)}
                      title={`${sortOptions.find(opt => opt.value === sortField)?.label} (${sortDirection === 'asc' ? t('ascending') : t('descending')})`}
                    >
                      {getSortIcon(sortField)}
                    </Button>
                  </div>
                  <small className="text-muted mt-1 d-block">
                    {t('sortedBy', { field: sortOptions.find(opt => opt.value === sortField)?.label, direction: sortDirection === 'asc' ? t('asc') : t('desc') })}
                  </small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentNursery && !loading && (
          <Card>
            <Card.Body className="p-0">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedCameras.length > 0 ? (
                  paginatedCameras.map((camera) => (
                    <CameraCard
                      key={camera.id}
                      camera={camera}
                      onViewDetails={handleViewDetails}
                      onEdit={handleEditCamera}
                      modalLoading={modalLoading}
                      canEdit={canEdit('cameras')}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <CameraIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, nursery: currentNursery.description })
                        : t('noneConfigured', { nursery: currentNursery.description })
                      }
                    </p>
                    {!searchTerm && canAdd('cameras') && (
                      <Button variant="primary" onClick={handleShowCreateModal}>
                        <PlusIcon />
                        {t('addFirstCamera')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('status')}
                          title={t('sortByStatus')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {tc('labels.status')}
                            {getSortIcon('status')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('name')}
                          title={t('sortByCameraName')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('cameraName')}
                            {getSortIcon('name')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('camera_number')}
                          title={t('sortByCameraNumber')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('cameraNumber')}
                            {getSortIcon('camera_number')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('location')}
                          title={t('sortByLocation')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('location')}
                            {getSortIcon('location')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('classroom_name')}
                          title={t('sortByClassroom')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {tc('labels.classroom')}
                            {getSortIcon('classroom_name')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('latest_image_timestamp')}
                          title={t('sortByLastImage')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('lastImage')}
                            {getSortIcon('latest_image_timestamp')}
                          </div>
                        </th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCameras.length > 0 ? (
                        paginatedCameras.map((camera) => (
                          <CameraTableRow
                            key={camera.id}
                            camera={camera}
                            onViewDetails={handleViewDetails}
                            onEdit={handleEditCamera}
                            modalLoading={modalLoading}
                            nurseryTimezone={nurseryTimezone}
                            canEdit={canEdit('cameras')}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-5">
                            <CameraIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, nursery: currentNursery.description })
                                : t('noneConfigured', { nursery: currentNursery.description })
                              }
                            </p>
                            {!searchTerm && canAdd('cameras') && (
                              <Button variant="primary" onClick={handleShowCreateModal}>
                                <PlusIcon />
                                {t('addFirstCamera')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {sortedCameras.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, sortedCameras.length), total: sortedCameras.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {/* Items per page selector */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!currentNursery && !loading && (
          <Card>
            <Card.Body className="text-center py-5">
              <h5>{tc('messages.selectNurseryTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectNurseryBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create/Edit Camera Modal */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {createModalSuccess && (
                <Alert variant="success" className="mb-3">
                  {createModalSuccess}
                </Alert>
              )}
              
              {createModalError && (
                <Alert variant="danger" className="mb-3">
                  {createModalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('numberLabel')} {!isEditMode ? '*' : ''}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.camera_number}
                      onChange={(e) => handleFormChange('camera_number', e.target.value)}
                      placeholder={t('numberPlaceholder')}
                      required={!isEditMode}
                      readOnly={isEditMode}
                      disabled={isEditMode}
                      className="bg-light"
                    />
                    {isEditMode && (
                      <Form.Text className="text-muted">
                        {t('numberReadonlyHint')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('locationLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleFormChange('location', e.target.value)}
                      placeholder={t('locationPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nurseryLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentNursery ? `${currentNursery.name} (${currentNursery.nursery_id})` : ''}
                      readOnly
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      {isEditMode 
                        ? t('nurseryEditHint')
                        : t('nurseryCreateHint')
                      }
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('edgeDeviceLabel')}</Form.Label>
                    <Form.Select
                      value={formData.edge_device_id}
                      onChange={(e) => handleFormChange('edge_device_id', parseInt(e.target.value))}
                      required
                      disabled={edgeDevicesLoading}
                    >
                      <option value={0}>
                        {edgeDevicesLoading ? t('loadingEdgeDevices') : t('selectEdgeDevice')}
                      </option>
                      {edgeDevices.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                    </Form.Select>
                    {edgeDevices.length === 0 && !edgeDevicesLoading && (
                      <Form.Text className="text-warning">
                        {t('noEdgeDevices')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('classroomLabel')}</Form.Label>
                    <Form.Select
                      value={formData.classroom_id}
                      onChange={(e) => handleFormChange('classroom_id', parseInt(e.target.value))}
                      required
                      disabled={classroomsLoading}
                    >
                      <option value={0}>
                        {classroomsLoading ? t('loadingClassrooms') : t('selectClassroom')}
                      </option>
                      {classrooms.map((classroom) => (
                        <option key={classroom.id} value={classroom.id}>
                          {classroom.name}
                        </option>
                      ))}
                    </Form.Select>
                    {classrooms.length === 0 && !classroomsLoading && (
                      <Form.Text className="text-warning">
                        {t('noClassrooms')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('ipLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) => handleFormChange('ip_address', e.target.value)}
                      placeholder={t('ipPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('portLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.port}
                      onChange={(e) => handleFormChange('port', parseInt(e.target.value))}
                      placeholder={t('portPlaceholder')}
                      min="1"
                      max="65535"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('usernameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      placeholder={t('usernamePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('passwordLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="camera-status"
                      label={t('cameraActive')}
                      checked={formData.status}
                      onChange={(e) => handleFormChange('status', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseCreateModal} disabled={createModalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={createModalLoading || formData.edge_device_id === 0 || formData.classroom_id === 0}
              >
                {createModalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? tc('loading.updating') : tc('loading.creating')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <EditIcon /> : <PlusIcon />}
                    {isEditMode ? t('updateCamera') : t('createCamera')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Bootstrap Camera Details Modal */}
        <Modal 
          show={modalOpen} 
          onHide={handleCloseModal}
          size="lg"
          fullscreen="lg-down"
          scrollable
        >
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center">
              {t('detailsTitle')}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-0">
            {modalLoading ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <span>{t('loadingDetails')}</span>
              </div>
            ) : selectedCamera ? (
              <Container fluid className="p-4">
                {/* Latest Image Section */}
                {selectedCamera.latest_image_url && (
                  <Card className="mb-4">
                    <Card.Header className="bg-success text-white">
                      <h6 className="mb-0">
                        <CameraIcon />
                        {t('lastImage')}
                      </h6>
                    </Card.Header>
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <small className="text-muted text-uppercase fw-bold d-block">{t('timestamp')}</small>
                        <div className="fw-medium">
                          {selectedCamera.latest_image_timestamp ? formatDateTime(selectedCamera.latest_image_timestamp) : 'N/A'}
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-light">
                        <img 
                          src={selectedCamera.latest_image_url} 
                          alt={t('latestCapture')} 
                          className="img-fluid rounded shadow"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLDivElement;
                            if (nextElement) nextElement.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none' }} className="text-muted p-4">
                          <CameraIcon />
                          <div className="mt-2">{t('unableLoadImage')}</div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Basic Information */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0 text-primary">
                      <CameraIcon />
                      {t('basicInfo')}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{t('cameraName')}</small>
                          <div className="fw-medium">{renderCellValue(selectedCamera.name)}</div>
                        </div>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{t('cameraNumber')}</small>
                          <div className="fw-medium">
                            <code>{renderCellValue(selectedCamera.camera_number)}</code>
                          </div>
                        </div>
                        <div className="d-flex align-items-start">
                          <LocationIcon />
                          <div className="ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('location')}</small>
                            <div>{renderCellValue(selectedCamera.location)}</div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{tc('labels.status')}</small>
                          <div>
                            <Badge bg={selectedCamera.status ? 'success' : 'danger'} className="mt-1">
                              {selectedCamera.status ? tc('status.active') : tc('status.inactive')}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <small className="text-muted text-uppercase fw-bold d-block">{tc('labels.classroom')}</small>
                          <div>{renderCellValue(selectedCamera.classroom_name)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>



                {/* Authentication Information */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0 text-primary">
                      <LockIcon />
                      {t('auth')}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <PersonIcon />
                          <div className="flex-grow-1 ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('username')}</small>
                            <div className="bg-light p-2 rounded mt-1">
                              <code>{renderCellValue(selectedCamera.username)}</code>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <LockIcon />
                          <div className="flex-grow-1 ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('password')}</small>
                            <div className="bg-light p-2 rounded mt-1">
                              <code className="text-break">{renderCellValue(selectedCamera.password)}</code>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>


              </Container>
            ) : null}
          </Modal.Body>
          
          <Modal.Footer className="border-top">
            <Button variant="primary" onClick={handleCloseModal} className="px-4">
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default memo(Cameras); 
